import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { TechDataAPI, extractError } from "../lib/api";
import { TechDataMarkdownView } from "../components/TechDataMarkdownView";

export default function TechDataDetailPage() {
    const { id } = useParams();
    const { permissions } = useAuth();
    const canWrite = (permissions || []).includes("techdata:write");
    const [entry, setEntry] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        TechDataAPI.get(id).then(setEntry).catch((e) => setErr(extractError(e)));
    }, [id]);

    if (err) return <div data-testid="tech-data-error" className="text-sm text-rose-600">{err}</div>;
    if (!entry) return <div className="text-sm opacity-60">Loading…</div>;

    const units = entry.units || {};
    const unitKeys = Object.keys(units);

    return (
        <div data-testid="tech-data-detail-page" className="space-y-5 max-w-3xl">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-widest opacity-60">
                        {[entry.make, entry.model, entry.year_range_start && (entry.year_range_end ? `${entry.year_range_start}-${entry.year_range_end}` : `${entry.year_range_start}+`)].filter(Boolean).join(" · ") || "Generic"}
                        {entry.is_system ? "" : " · custom"}
                    </div>
                    <h1 data-testid="tech-data-detail-title" className="text-2xl sm:text-3xl font-bold">{entry.title}</h1>
                </div>
                <Button asChild variant="outline">
                    <Link to="/tech-data" data-testid="tech-data-back">← Back</Link>
                </Button>
            </div>

            <div className="flex flex-wrap gap-2">
                <span className="text-xs uppercase tracking-widest rounded-full bg-muted px-2 py-0.5">{entry.category}</span>
                {!entry.is_system && (
                    <span className="text-xs uppercase tracking-widest rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 px-2 py-0.5">tenant-custom</span>
                )}
                {entry.provider && entry.provider !== "custom" && (
                    <span className="text-xs uppercase tracking-widest rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200 px-2 py-0.5">{entry.provider}</span>
                )}
                {!entry.is_active && (
                    <span className="text-xs uppercase tracking-widest rounded-full bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200 px-2 py-0.5">inactive</span>
                )}
            </div>

            <div className="rounded-xl border bg-card p-4">
                <TechDataMarkdownView body={entry.body_markdown} />
            </div>

            {unitKeys.length > 0 && (
                <div className="rounded-xl border bg-card p-4">
                    <h3 className="text-xs uppercase tracking-widest opacity-60 mb-2">Specs</h3>
                    <table data-testid="tech-data-spec-table" className="w-full">
                        <tbody>
                            {unitKeys.map((k) => (
                                <tr key={k} data-testid="tech-data-spec-row" className="border-b last:border-0">
                                    <td className="py-1.5 pr-4 text-sm opacity-70">{k.replace(/_/g, " ")}</td>
                                    <td data-testid={`tech-data-spec-value-${k}`} className="py-1.5 text-sm font-mono">{String(units[k])}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {entry.source_url && (
                <div className="text-xs opacity-60">Source: {entry.source_url}</div>
            )}

            {canWrite && !entry.is_system && (
                <div>
                    <Button asChild variant="outline">
                        <Link to={`/tech-data/${entry.id}/edit`} data-testid="tech-data-edit">Edit</Link>
                    </Button>
                </div>
            )}
        </div>
    );
}
